import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { ReportMultiSelect } from './ReportMultiSelect';
import { setFlashToast } from '../../utils/flashToast';

export type ReportExportFormat = 'pdf' | 'excel';

const DEFAULT_EXPORT_FORMAT: ReportExportFormat = 'pdf';

export function ReportExportToolbar({
  onGenerate,
  disabled = false,
}: {
  onGenerate?: (format: ReportExportFormat) => Promise<void>;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [selectedFormat, setSelectedFormat] = useState<ReportExportFormat>(DEFAULT_EXPORT_FORMAT);
  const [generating, setGenerating] = useState(false);

  const exportOptions = useMemo(
    () => [t('reports.exportAsPdf'), t('reports.exportAsExcel')] as const,
    [t],
  );

  const selectedLabel =
    selectedFormat === 'pdf' ? t('reports.exportAsPdf') : t('reports.exportAsExcel');

  const handleFormatChange = (labels: string[]) => {
    const picked = labels[0];
    if (picked === t('reports.exportAsExcel')) {
      setSelectedFormat('excel');
      return;
    }
    setSelectedFormat(DEFAULT_EXPORT_FORMAT);
  };

  const runGenerate = async () => {
    if (onGenerate) {
      setGenerating(true);
      try {
        await onGenerate(selectedFormat);
      } finally {
        setGenerating(false);
      }
      return;
    }

    setGenerating(true);
    window.setTimeout(() => {
      setGenerating(false);
      setFlashToast({ message: t('reports.generateSuccess'), variant: 'success' });
    }, 600);
  };

  const actionsBusy = generating || disabled;

  return (
    <div className="rpt-actions">
      <div className="rpt-export-as">
        <ReportMultiSelect
          label={t('reports.exportAs')}
          value={[selectedLabel]}
          options={exportOptions}
          placeholder={t('reports.exportAsPdf')}
          single
          searchable={false}
          showCheckboxes={false}
          disabled={actionsBusy}
          onChange={handleFormatChange}
        />
      </div>
      <Button variant="primary" disabled={actionsBusy} onClick={() => void runGenerate()}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {generating ? t('reports.generating') : t('reports.generateReport')}
      </Button>
    </div>
  );
}
